import {
  CallHistoryAiCall,
  CallHistoryStafferCall,
  QuoMessageDirection,
  QuoMessageResponse,
} from "@/types";

// Consecutive messages from the same sender within this window collapse into a single
// sender group so the sender header doesn't repeat on rapid back-and-forth.
export const TIME_GROUP_THRESHOLD_MS = 5 * 60 * 1000;

export const QUEUED_STATUS_LABELS = [
  "Bay Watching",
  "Blooming",
  "Chirping",
  "ChrisLunoing",
  "Clacking",
  "Corbining",
  "DomDollaing",
  "FredAgaining",
  "Gandering",
  "GGGing",
  "Gonging",
  "Hatching",
  "Hooping",
  "JohnSummitting",
  "Lifting",
  "Memeing",
  "Monstering",
  "MooDenging",
  "Quoing",
  "Slacking",
  "Staffing",
  "Tiestoing",
  "Titaning",
  "Tweaking",
  "Vibing",
  "Wrangling",
  "Yeehawing",
];

export interface SenderGroup {
  direction: QuoMessageDirection;
  messages: QuoMessageResponse[];
  senderName: string | null;
}

// Stable hash so each (userId, body) gets the same queued label across re-renders.
export const getStableQueuedLabel = (seed: string): string => {
  let hash = 0;
  for (const char of seed) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return QUEUED_STATUS_LABELS[hash % QUEUED_STATUS_LABELS.length];
};

// Group consecutive messages from the same sender within TIME_GROUP_THRESHOLD_MS
export const groupBySender = (
  messages: QuoMessageResponse[],
): SenderGroup[] => {
  const groups: SenderGroup[] = [];

  for (const message of messages) {
    const lastGroup = groups.at(-1);

    const timeSinceLast = lastGroup
      ? new Date(message.sentAt).getTime() -
        new Date(
          lastGroup.messages[lastGroup.messages.length - 1].sentAt,
        ).getTime()
      : Infinity;

    const sameDirection = lastGroup?.direction === message.direction;
    const sameUser = lastGroup?.messages[0].userId === message.userId;

    if (
      lastGroup &&
      sameDirection &&
      sameUser &&
      timeSinceLast < TIME_GROUP_THRESHOLD_MS
    ) {
      lastGroup.messages.push(message);
    } else {
      const senderName =
        message.direction === QuoMessageDirection.OUTGOING
          ? `${message.user.firstName} ${message.user.lastName}`
          : `To ${message.user.firstName} ${message.user.lastName}`;

      groups.push({
        direction: message.direction,
        messages: [message],
        senderName,
      });
    }
  }

  return groups;
};

export enum QuoTimelineItemKind {
  AI_CALL = "AI_CALL",
  CALL = "CALL",
  MESSAGE = "MESSAGE",
}

export type QuoTimelineItem =
  | { at: string; call: CallHistoryStafferCall; kind: QuoTimelineItemKind.CALL }
  | {
      aiCall: CallHistoryAiCall;
      at: string;
      kind: QuoTimelineItemKind.AI_CALL;
    }
  | {
      at: string;
      kind: QuoTimelineItemKind.MESSAGE;
      message: QuoMessageResponse;
    };

// Merge messages, staffer calls, and AI calls into one chronological stream,
// grouped by calendar day ascending (oldest day first, items within a day
// ascending).
export const groupTimelineByDate = (
  messages: QuoMessageResponse[],
  calls: CallHistoryStafferCall[],
  aiCalls: CallHistoryAiCall[] = [],
): { date: string; items: QuoTimelineItem[] }[] => {
  const items: QuoTimelineItem[] = [
    ...messages.map((message) => ({
      at: message.sentAt,
      kind: QuoTimelineItemKind.MESSAGE as const,
      message,
    })),
    ...calls.map((call) => ({
      at: call.startedAt,
      call,
      kind: QuoTimelineItemKind.CALL as const,
    })),
    ...aiCalls.map((aiCall) => ({
      aiCall,
      at: aiCall.scheduledDateTime,
      kind: QuoTimelineItemKind.AI_CALL as const,
    })),
  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  const groups: { date: string; items: QuoTimelineItem[] }[] = [];
  for (const item of items) {
    const dateStr = new Date(item.at).toDateString();
    const lastGroup = groups.at(-1);

    if (lastGroup && new Date(lastGroup.date).toDateString() === dateStr) {
      lastGroup.items.push(item);
    } else {
      groups.push({ date: item.at, items: [item] });
    }
  }

  return groups;
};

// Group messages by calendar day in descending order.
export const groupMessagesByDate = (
  messages: QuoMessageResponse[],
): { date: string; messages: QuoMessageResponse[] }[] => {
  const groups: { date: string; messages: QuoMessageResponse[] }[] = [];
  const sorted = [...messages].sort(
    (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime(),
  );

  for (const message of sorted) {
    const dateStr = new Date(message.sentAt).toDateString();
    const lastGroup = groups.at(-1);

    if (lastGroup && new Date(lastGroup.date).toDateString() === dateStr) {
      lastGroup.messages.push(message);
    } else {
      groups.push({ date: message.sentAt, messages: [message] });
    }
  }

  for (const group of groups) {
    group.messages.reverse();
  }

  return groups;
};
